'use client';

import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useAppStore } from '@/stores/app-store';
import { t } from '@/lib/i18n';
import { DEV_TYPES, FIELD_META, fieldLabel, deviceFieldKeys } from '@/lib/device-types';
import { fmtMetric } from '@/lib/helpers';
import { getAIInsights } from '@/lib/ai-engine';
import { AIInsights, Device, DeviceData, DeviceTypeId } from '@/lib/types';
import { mapFieldName } from '@/lib/db-adapters';
import { isOnlineStatus, NO_VALUE } from '@/lib/device-status';
import DeviceFieldChart, { INTERVAL_OPTIONS, exportChartPdfById, exportChartPngById, MONITOR_CHART_ID } from '@/components/widgets/DeviceFieldChart';
import { fieldIcon } from '@/components/WidgetRenderer';
import MonitorEnergyTable, { deviceEnergyFields } from '@/components/pages/MonitorEnergyTable';

// Today as YYYY-MM-DD (for the default date-range inputs).
function monitorToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Distinct, high-contrast palette for metric cards + chart lines (cycled by field index).
/*
 * Field grouping for the chip strip. A 3-phase meter reports 16+ values and a
 * flat wrapping row of them is unreadable — voltages, currents, powers and
 * energies all look alike. Group by what the value IS, matching on the key AND
 * the device's own field name (DB-driven names vary per meter model).
 *
 * The UNIT decides first: a field called "L1" carrying Amp is a current no
 * matter what the meter's owner typed. The name is only the fallback for
 * unitless fields (Total PF ships with a blank unit).
 *
 * Order matters in both passes: energy is claimed before power so kWh/kVArh
 * aren't caught by the kW/kVAr patterns inside them, and power factor and
 * frequency before power so "Total PF" doesn't land in Power.
 */
type FieldGroupId = 'voltage' | 'current' | 'power' | 'energy' | 'quality' | 'other';
const FIELD_GROUP_ORDER: FieldGroupId[] = ['voltage', 'current', 'power', 'energy', 'quality', 'other'];
// One accent per group, matching the icon colours the same metrics get
// elsewhere, so a group card is recognisable before its label is read.
const FIELD_GROUP_COLOR: Record<FieldGroupId, string> = {
  voltage: '#F5A623',
  current: '#8B5CF6',
  power: '#10B77F',
  energy: '#06B6D4',
  quality: '#0EA5E9',
  other: '#94A3B8',
};
const FIELD_GROUP_LABEL: Record<FieldGroupId, { en: string; th: string }> = {
  voltage: { en: 'Voltage', th: 'แรงดัน' },
  current: { en: 'Current', th: 'กระแส' },
  power: { en: 'Power', th: 'กำลังไฟ' },
  energy: { en: 'Energy', th: 'พลังงาน' },
  quality: { en: 'Power quality', th: 'คุณภาพไฟ' },
  other: { en: 'Other', th: 'อื่น ๆ' },
};
function fieldGroupOf(haystack: string, unit?: string): FieldGroupId {
  const u = (unit || '').trim().toLowerCase().replace(/\s+/g, '');
  if (u) {
    if (/^[kmg]?(w|va|var)h$/.test(u)) return 'energy';              // kWh, kVArh, MWh
    if (/^[km]?(a|amp|amps|ampere|amperes)$/.test(u)) return 'current';
    if (/^[km]?(v|volt|volts|vac|vdc)$/.test(u)) return 'voltage';
    if (/^[km]?(w|va|var|watt|watts)$/.test(u)) return 'power';      // kW, kVA, kVAr
    if (/^(hz|hertz)$/.test(u)) return 'quality';
  }
  const h = haystack.toLowerCase();
  if (/\bpf\b|power.?factor|freq|\bhz\b|thd|harmonic|unbalance/.test(h)) return 'quality';
  if (/energy|kwh|kvarh|\bdaily\b|\bmonthly\b/.test(h)) return 'energy';
  if (/volt|vll|vln|\bv[0-9]/.test(h)) return 'voltage';
  if (/current|\bamp|\bi[0-9]/.test(h)) return 'current';
  if (/power|watt|\bkw\b|\bkva\b|\bkvar\b|demand|load/.test(h)) return 'power';
  return 'other';
}
// Below this a flat row is easier to scan than a set of one-chip groups.
const FIELD_GROUPING_MIN = 7;
// Past this a group switches to one-line chips and claims a row of its own —
// Power Quality on a power analyser carries 23 dimensionless percentages.
const FIELD_GROUP_DENSE_MIN = 8;

const FIELD_PALETTE = ['#6366F1', '#12B981', '#F59E0B', '#EF4444', '#0EA5E9', '#A855F7', '#EC4899', '#14B8A6', '#F97316', '#8B5CF6', '#22C55E', '#3B82F6', '#E11D48', '#06B6D4'];

// Shape of /api/devices rows (all devices the user can access, cross-plant).
interface OvDev { mac_id: string; name: string; type: string; timeout_status: string; fields?: { name: string; value: string; unit: string }[]; }
// Convert an /api/devices row into a store Device so the standalone Monitor
// dropdown can list every accessible device (not just the current plant's).
function ovToDevice(d: OvDev): Device {
  const data: DeviceData = {}; const fieldNames: Record<string, string> = {}; const units: Record<string, string> = {};
  for (const f of d.fields || []) {
    const key = mapFieldName(f.name);
    const n = parseFloat(String(f.value).replace(/[^0-9.\-]/g, ''));
    if (!isNaN(n)) data[key] = n;
    fieldNames[key] = f.name;
    if (f.unit) units[key] = f.unit;
  }
  return {
    sn: d.mac_id, type: (d.type as DeviceTypeId) || 'env_sensor', name: d.name,
    st: isOnlineStatus(d.timeout_status) ? 'online' : 'offline',
    data, fieldNames, units,
  };
}

interface EventRow {
  sv: 'critical' | 'warning' | 'info';
  time: string;
  title: string;
  desc: string;
  device: string;
}

// Shape returned by GET /api/devices/[macId]/events
interface DbEvent {
  id: number;
  timestamp: string | null;
  severity: 'critical' | 'warning' | 'info';
  alarmType: string;
  fieldName: string;
  message: string;
  count: number | null;
}

const SV_STYLE: Record<EventRow['sv'], { bg: string; fg: string }> = {
  critical: { bg: 'var(--red-dim)', fg: 'var(--red)' },
  warning: { bg: 'var(--amber-dim)', fg: 'var(--amber)' },
  info: { bg: 'var(--blue-dim)', fg: 'var(--blue)' },
};

const EVENTS_PAGE_SIZE = 10;

export default function MonitorPage() {
  const currentPlant = useAppStore((s) => s.currentPlant);
  const plants = useAppStore((s) => s.plants);
  const lang = useAppStore((s) => s.lang);
  const dbUser = useAppStore((s) => s.dbUser);
  const monitorDevSN = useAppStore((s) => s.monitorDevSN);
  const monitorDevice = useAppStore((s) => s.monitorDevice);

  const plant = currentPlant ? plants[currentPlant] : null;
  // Standalone (no plant): load every device the user can access so the dropdown
  // lets them switch between all their devices — not just the one clicked.
  const [globalDevices, setGlobalDevices] = useState<Device[]>([]);
  useEffect(() => {
    if (plant || !dbUser) return; // only when standalone
    let cancelled = false;
    fetch('/api/devices')
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!cancelled && Array.isArray(j)) setGlobalDevices(j.map(ovToDevice)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [plant, dbUser]);

  // Prefer the current plant's devices; else all accessible devices; else the
  // single device passed in (e.g. clicked from the cross-plant Device Overview).
  const devices = useMemo(
    () => (plant?.devices?.length ? plant.devices
      : globalDevices.length ? globalDevices
      : (monitorDevice ? [monitorDevice] : [])),
    [plant, globalDevices, monitorDevice]
  );

  const [selectedSN, setSelectedSN] = useState<string | null>(null);

  // When navigated here with a device pre-selected (e.g. clicking a card on the
  // device overview), adopt that device as the active selection.
  useEffect(() => {
    if (monitorDevSN) {
      setSelectedSN(monitorDevSN);
      setSelectedFields([]);
      setEventsPage(1);
      setFieldsExpanded(false);
    }
  }, [monitorDevSN]);
  // One or more fields can be selected to overlay/compare on the chart.
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  // Fallback range when no custom date span is applied (range chips removed in
  // favor of the granularity dropdown + date pickers + Plot).
  const chartRange = '1d';
  const [chartInterval, setChartInterval] = useState('raw');
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingPng, setExportingPng] = useState(false);
  // Devices can expose up to 50 fields; collapse the chip row to a few rows and
  // reveal the rest behind a "show all" toggle.
  const [fieldsExpanded, setFieldsExpanded] = useState(false);

  // Custom date range. Defaults to the current day (00:00 → now) so the inputs
  // are pre-filled with today instead of an empty mm/dd/yyyy.
  const [customFrom, setCustomFrom] = useState(monitorToday);
  const [customTo, setCustomTo] = useState(monitorToday);
  const customActive = !!customFrom && !!customTo;

  // The chart + exports read the *applied* values; the date/granularity inputs
  // above only take effect when the user clicks "Plot" (like the legacy Monitor).
  // Applied defaults to today too, so the first render already plots today.
  const [appliedFrom, setAppliedFrom] = useState(monitorToday);
  const [appliedTo, setAppliedTo] = useState(monitorToday);
  const [appliedInterval, setAppliedInterval] = useState('raw');
  const appliedActive = !!appliedFrom && !!appliedTo;
  const applyPlot = () => {
    setAppliedFrom(customFrom);
    setAppliedTo(customTo);
    setAppliedInterval(chartInterval);
  };

  // Derive the effective device: the clicked one if still present, else the
  // first device of the plant. Avoids a selection-sync effect when the plant
  // (and its device list) changes.
  const device = useMemo(() => {
    if (devices.length === 0) return null;
    return devices.find((d) => d.sn === selectedSN) || devices[0];
  }, [devices, selectedSN]);
  const devType = device ? DEV_TYPES[device.type] : null;
  const isOnline = device?.st === 'online';
  // Every configured field, not just the ones in the latest reading — chips with
  // no current value (or an offline device's live fields) show "—" and stay clickable so their history still charts.
  const dataFields = useMemo(() => {
    const keys = deviceFieldKeys(device);
    // Hide derived energy accumulators (daily/monthly kWh) on non-energy devices
    // — e.g. water-level sensors — where they're always 0 and irrelevant.
    const ENERGY_TYPES = ['smart_meter', 'power_analyzer', 'energy_saver', 'inverter_solar', 'mdb_sensor'];
    return device && ENERGY_TYPES.includes(device.type) ? keys : keys.filter((k) => k !== 'daily' && k !== 'monthly');
  }, [device]);

  // Effective selection: the chosen fields still present on this device, or the
  // first field as a fallback (the chart always needs at least one series).
  const activeFields = useMemo(() => {
    const valid = selectedFields.filter((k) => dataFields.includes(k));
    return valid.length ? valid : (dataFields[0] ? [dataFields[0]] : []);
  }, [selectedFields, dataFields]);

  const toggleField = (key: string) => {
    setSelectedFields((prev) => {
      const cur = prev.filter((k) => dataFields.includes(k));
      return cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
    });
  };

  // Human-readable date-range label for exports (usercomment.pdf A-3): show the
  // actual date span rather than a code like "7D". Thai mode uses Buddhist-era
  // 2-digit years, e.g. "วันที่ 11/06/69 ถึงวันที่ 12/06/69".
  const rangeLabel = () => {
    const pad = (n: number) => String(n).padStart(2, '0');
    const fmtDate = (d: Date) => {
      const yr = lang === 'th'
        ? pad((d.getFullYear() + 543) % 100)
        : String(d.getFullYear());
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${yr}`;
    };
    let from: Date, to: Date;
    if (appliedActive) {
      from = new Date(`${appliedFrom}T00:00:00`);
      to = new Date(`${appliedTo}T00:00:00`);
    } else {
      const days = parseInt(chartRange, 10) || 1;
      to = new Date();
      from = new Date(to.getTime() - days * 86400000);
    }
    return lang === 'th'
      ? `วันที่ ${fmtDate(from)} ถึงวันที่ ${fmtDate(to)}`
      : `${fmtDate(from)} – ${fmtDate(to)}`;
  };

  // Identifying header info stamped onto every export (usercomment.pdf A-1):
  // chart title, user, organization (the plant/site), device name, SN. Field
  // labels are localized (A-3) and the range is shown as a date span.
  const exportInfo = () => ({
    chartTitle: activeFields.map((k) => fieldLabel(k, lang, device?.fieldNames?.[k])).join(', '),
    userName: dbUser?.name || '',
    org: plant?.name || '',
    deviceName: device?.name || '',
    sn: device?.sn || '',
    rangeLabel: rangeLabel(),
  });

  // Export the active fields' time series (current range/interval) to .xlsx.
  // Re-fetches the same data endpoint the chart uses, then builds a sheet:
  // an identifying header block, then a Time column + one column per selected
  // field. xlsx is lazy-loaded.
  const exportExcel = async () => {
    if (!device || !dbUser || exporting || activeFields.length === 0) return;
    setExporting(true);
    try {
      const qs = new URLSearchParams();
      if (appliedActive) { qs.set('from', appliedFrom); qs.set('to', appliedTo); }
      else qs.set('range', chartRange);
      qs.set('interval', appliedInterval);
      qs.set('keys', activeFields.join(','));
      // Every row, not the chart's thinned 500 points.
      qs.set('full', '1');
      const res = await fetch(`/api/devices/${encodeURIComponent(device.sn)}/data?${qs.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const labels: string[] = data.labels || [];
      const timestamps: number[] = data.timestamps || [];
      const apiSeries: { key: string; name: string; unit: string; data: (number | null)[] }[] = data.series || [];

      const cols = activeFields.map((k) => {
        const s = apiSeries.find((ss) => ss.key === k);
        const label = fieldLabel(k, lang, device?.fieldNames?.[k]) || s?.name || k;
        // DB-first unit (device's configured data_unit), FIELD_META as fallback.
        const unit = device?.units?.[k] || s?.unit || FIELD_META[k]?.unit || '';
        return { header: unit ? `${label} (${unit})` : label, data: s?.data || [] };
      });

      const fmt = (ts: number) => {
        const d = new Date(ts);
        const p = (n: number) => String(n).padStart(2, '0');
        // Seconds included: raw rows land at the meter's own times (00:00:18,
        // 00:01:49 …) and minutes alone made two readings look like one.
        return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())}`;
      };

      const rowCount = timestamps.length || labels.length;
      const L = (th: string, en: string) => (lang === 'th' ? th : en);
      const info = exportInfo();
      const aoa: (string | number | null)[][] = [
        [L('หัวข้อกราฟ', 'Chart'), info.chartTitle],
        [L('ผู้ใช้', 'User'), info.userName],
        [L('หน่วยงาน', 'Organization'), info.org],
        [L('อุปกรณ์', 'Device'), info.deviceName],
        ['SN', info.sn],
        [L('ช่วงเวลา', 'Range'), info.rangeLabel],
        ...(data.meta?.truncated
          ? [[L('หมายเหตุ', 'Note'), L(`ข้อมูลเกิน ${Number(data.meta.maxRows).toLocaleString()} แถว — ส่งออกเฉพาะ ${Number(data.meta.maxRows).toLocaleString()} แถวแรก กรุณาเลือกช่วงเวลาสั้นลง`, `More than ${Number(data.meta.maxRows).toLocaleString()} rows — only the first ${Number(data.meta.maxRows).toLocaleString()} exported; choose a shorter range`)]]
          : []),
        [],
        [L('เวลา', 'Time'), ...cols.map((c) => c.header)],
      ];
      for (let i = 0; i < rowCount; i++) {
        const t = timestamps[i] != null ? fmt(timestamps[i]) : (labels[i] || '');
        aoa.push([t, ...cols.map((c) => (c.data[i] == null ? null : Number(c.data[i])))]);
      }

      const XLSX = await import('xlsx');
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Data');
      const safe = (s: string) => s.replace(/[^a-zA-Z0-9_-]+/g, '_').slice(0, 40);
      // Range portion as an actual date span (YYYY-MM-DD_YYYY-MM-DD) instead of
      // a code like "7d". Kept ASCII so the file name stays clean.
      let fileRange: string;
      if (appliedActive) {
        fileRange = `${appliedFrom}_${appliedTo}`;
      } else {
        const pad = (n: number) => String(n).padStart(2, '0');
        const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        const days = parseInt(chartRange, 10) || 1;
        const to = new Date();
        const from = new Date(to.getTime() - days * 86400000);
        fileRange = `${iso(from)}_${iso(to)}`;
      }
      XLSX.writeFile(wb, `${safe(device.name || 'device')}_${safe(activeFields.join('-'))}_${fileRange}.xlsx`);
    } catch (e) {
      console.error('Excel export failed', e);
      alert(lang === 'th' ? 'ส่งออก Excel ไม่สำเร็จ' : 'Excel export failed');
    } finally {
      setExporting(false);
    }
  };

  // Export the rendered chart (current fields/range) to PDF. Captures the live
  // chart image via its id, so it works in both DB and demo mode.
  const exportPdf = async () => {
    if (!device || exportingPdf || activeFields.length === 0) return;
    setExportingPdf(true);
    try {
      const info = exportInfo();
      await exportChartPdfById(MONITOR_CHART_ID, {
        deviceName: device.name || 'Device',
        fieldLabel: activeFields.map((k) => fieldLabel(k, lang, device?.fieldNames?.[k])).join(', '),
        unit: activeFields.length === 1 ? (device?.units?.[activeFields[0]] || FIELD_META[activeFields[0]]?.unit || '') : '',
        range: info.rangeLabel,
        chartTitle: info.chartTitle,
        userName: info.userName,
        org: info.org,
        sn: info.sn,
        lang,
      });
    } finally {
      setExportingPdf(false);
    }
  };

  // Export the rendered chart to PNG with the same header block (A-2). Composes
  // the chart image + identifying header onto a canvas, so the .png carries the
  // same info as the Excel/PDF exports.
  const exportPng = async () => {
    if (!device || exportingPng || activeFields.length === 0) return;
    setExportingPng(true);
    try {
      const info = exportInfo();
      await exportChartPngById(MONITOR_CHART_ID, {
        deviceName: device.name || 'Device',
        fieldLabel: activeFields.map((k) => fieldLabel(k, lang, device?.fieldNames?.[k])).join(', '),
        unit: activeFields.length === 1 ? (device?.units?.[activeFields[0]] || FIELD_META[activeFields[0]]?.unit || '') : '',
        range: info.rangeLabel,
        chartTitle: info.chartTitle,
        userName: info.userName,
        org: info.org,
        sn: info.sn,
        lang,
      });
    } finally {
      setExportingPng(false);
    }
  };

  // Field chips shown when collapsed. Active fields are always kept visible so
  // selecting a field beyond the cap doesn't vanish on collapse.
  const FIELD_CHIP_LIMIT = 12;
  // Colour means "which plotted series is this", so only SELECTED fields get
  // one. Colouring all 40 fields of a power analyser by list position made the
  // palette repeat three times and paired unrelated metrics — the eye couldn't
  // follow a card to its line. The same map feeds the chart, so card and line
  // always agree.
  //
  // Slots are held in a ref so a colour stays with its field for as long as it
  // is selected: dropping series 1 must not re-colour series 2 and 3. A freed
  // slot is handed to the next field picked.
  const colorSlots = useRef<Record<string, number>>({});
  const fieldColorMap = useMemo(() => {
    const slots = colorSlots.current;
    for (const k of Object.keys(slots)) if (!activeFields.includes(k)) delete slots[k];
    const used = new Set(Object.values(slots));
    for (const k of activeFields) {
      if (slots[k] != null) continue;
      let i = 0;
      while (used.has(i)) i++;
      slots[k] = i;
      used.add(i);
    }
    const map: Record<string, string> = {};
    for (const k of activeFields) map[k] = FIELD_PALETTE[slots[k] % FIELD_PALETTE.length];
    return map;
  }, [activeFields]);

  const fieldsOverflow = dataFields.length > FIELD_CHIP_LIMIT;
  const visibleFields = useMemo(() => {
    if (fieldsExpanded || !fieldsOverflow) return dataFields;
    const head = dataFields.slice(0, FIELD_CHIP_LIMIT);
    for (const k of activeFields) if (!head.includes(k)) head.push(k);
    return head;
  }, [dataFields, fieldsExpanded, fieldsOverflow, activeFields]);

  // Chips bucketed by kind, keeping each field's original order inside its
  // group. Only worth it once a device reports enough values to be confusing —
  // a 3-phase meter, typically — and once more than one bucket is in play.
  const fieldGroups = useMemo(() => {
    if (visibleFields.length < FIELD_GROUPING_MIN) return null;
    const buckets = new Map<FieldGroupId, string[]>();
    for (const k of visibleFields) {
      const g = fieldGroupOf(
        `${k} ${fieldLabel(k, lang, device?.fieldNames?.[k]) || ''}`,
        device?.units?.[k] || FIELD_META[k]?.unit,
      );
      const list = buckets.get(g);
      if (list) list.push(k);
      else buckets.set(g, [k]);
    }
    if (buckets.size < 2) return null;
    return FIELD_GROUP_ORDER.filter((g) => buckets.has(g)).map((g) => ({ id: g, keys: buckets.get(g)! }));
  }, [visibleFields, lang, device]);

  // Group header action: plot every field in the group, or clear them all if
  // they're already the selection — the "compare L1/L2/L3" shortcut.
  const toggleGroup = (keys: string[]) => {
    setSelectedFields((prev) => {
      const cur = prev.filter((k) => dataFields.includes(k));
      const allOn = keys.every((k) => cur.includes(k));
      if (allOn) {
        const rest = cur.filter((k) => !keys.includes(k));
        return rest.length ? rest : cur; // never leave the chart with no series
      }
      return [...cur, ...keys.filter((k) => !cur.includes(k))];
    });
  };

  // Event log:
  //  - DB mode: real per-device alarm/event log from usr_log_<mac> via the API.
  //  - Demo mode: rule-based AI insights for the plant (no timestamps).
  // Show the DB wall-clock as-is (no timezone shift), matching how the chart
  // endpoint renders timestamps. The MySQL session tz is SYSTEM (the server's
  // local zone) and mysql2 has no `timezone` set, so the driver Date carries
  // the wall-clock numbers as if UTC — read them back with the UTC getters and
  // they print exactly what the DB stored, independent of the browser's zone.
  const fmtTime = (ts: string | null) => {
    if (!ts) return '—';
    const d = new Date(ts);
    if (isNaN(d.getTime())) return '—';
    const p = (n: number) => String(n).padStart(2, '0');
    return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)}/${d.getUTCFullYear()} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
  };

  const [dbEvents, setDbEvents] = useState<DbEvent[]>([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsPage, setEventsPage] = useState(1);
  const deviceSN = device?.sn || null;
  const fetchEvents = useCallback(async () => {
    if (!dbUser || !deviceSN) { setDbEvents([]); setEventsTotal(0); return; }
    setEventsLoading(true);
    try {
      const res = await fetch(`/api/devices/${encodeURIComponent(deviceSN)}/events?page=${eventsPage}&pageSize=${EVENTS_PAGE_SIZE}`);
      if (res.ok) {
        const json = await res.json();
        setDbEvents(Array.isArray(json.events) ? json.events : []);
        setEventsTotal(Number(json.total) || 0);
      } else {
        setDbEvents([]); setEventsTotal(0);
      }
    } catch {
      setDbEvents([]); setEventsTotal(0);
    } finally {
      setEventsLoading(false);
    }
  }, [dbUser, deviceSN, eventsPage]);
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);
  // Keep the event log fresh while the page is open.
  useEffect(() => {
    if (!dbUser || !deviceSN) return;
    const id = setInterval(fetchEvents, 30000);
    return () => clearInterval(id);
  }, [dbUser, deviceSN, fetchEvents]);

  const mockInsights = useMemo(() => (!dbUser ? getAIInsights(plant) : null), [plant, dbUser]);

  // Current-page rows + total count. DB mode is paginated server-side; demo
  // mode slices the client-side insights array.
  const { events, totalEvents } = useMemo(() => {
    if (dbUser) {
      const devName = device?.name || '';
      const rows: EventRow[] = dbEvents.map((e) => ({
        sv: e.severity,
        time: fmtTime(e.timestamp),
        title: e.alarmType || e.fieldName || 'Event',
        desc: e.message || e.fieldName,
        device: devName,
      }));
      return { events: rows, totalEvents: eventsTotal };
    }
    const ins: AIInsights = mockInsights || { warnings: [], anomalies: [], predictions: [] };
    const all: EventRow[] = ins.warnings.map((w) => ({ sv: w.sv, time: '—', title: w.title, desc: w.desc, device: w.device }));
    ins.anomalies.forEach((a) => {
      all.push({ sv: (a.sv as EventRow['sv']) || 'info', time: '—', title: a.title, desc: a.desc, device: '' });
    });
    const start = (eventsPage - 1) * EVENTS_PAGE_SIZE;
    return { events: all.slice(start, start + EVENTS_PAGE_SIZE), totalEvents: all.length };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dbUser, dbEvents, eventsTotal, device?.name, mockInsights, eventsPage, lang]);

  const totalPages = Math.max(1, Math.ceil(totalEvents / EVENTS_PAGE_SIZE));
  const rangeStart = totalEvents === 0 ? 0 : (eventsPage - 1) * EVENTS_PAGE_SIZE + 1;
  const rangeEnd = Math.min(eventsPage * EVENTS_PAGE_SIZE, totalEvents);

  // Standalone-friendly: only block when there is genuinely no device to show
  // (no plant selected AND nothing passed from Device Overview).
  if (!device) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted, #94a3b8)', fontSize: 14 }}>
        {t('selectPlant', lang)}
      </div>
    );
  }

  const pagerBtn = (disabled: boolean): React.CSSProperties => ({
    width: 30, height: 30, borderRadius: 8, border: '1px solid var(--glass-border)',
    background: 'transparent', color: 'var(--text-primary)',
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.35 : 1,
    fontSize: 16, lineHeight: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  });
  const dateInputStyle: React.CSSProperties = {
    padding: '4px 10px', borderRadius: 8, fontSize: 11.5, fontWeight: 600, fontFamily: 'inherit',
    border: '1px solid var(--glass-border)', background: 'transparent', color: 'var(--text-primary)',
    outline: 'none', colorScheme: 'light dark',
  };

  return (
    <div style={{ padding: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Control bar: device picker + status */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
        <select
          value={device?.sn || ''}
          onChange={(e) => { setSelectedSN(e.target.value); setSelectedFields([]); setEventsPage(1); setFieldsExpanded(false); }}
          style={{
            padding: '9px 14px', borderRadius: 10, border: '1px solid var(--glass-border)', background: 'transparent',
            color: 'var(--text-primary)', fontSize: 14, fontWeight: 700, fontFamily: 'inherit', outline: 'none', cursor: 'pointer', maxWidth: 360,
          }}
        >
          {devices.length === 0 && <option value="">{t('noDevices', lang)}</option>}
          {devices.map((d) => <option key={d.sn} value={d.sn}>{d.name}</option>)}
        </select>
        {device && (
          <>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 11px', borderRadius: 999, fontSize: 12, fontWeight: 600,
              background: isOnline ? 'var(--green-dim)' : 'var(--red-dim)', color: isOnline ? 'var(--green)' : 'var(--red)',
            }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'currentColor', boxShadow: '0 0 8px currentColor' }} />
              {isOnline ? t('online', lang) : t('offline', lang)}
            </span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'monospace' }}>
              {device.sn}{devType ? ` · ${devType.name}` : ''}
            </span>
          </>
        )}
      </div>

      {/* Chart — full width */}
      {!device ? (
        <div style={{ padding: 56, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>{t('selectDeviceToView', lang)}</div>
      ) : dataFields.length === 0 ? (
        <div style={{ padding: 56, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>{t('noData', lang)}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: 16, borderRadius: 16, border: '1px solid var(--glass-border)', background: 'var(--bg-surface)' }}>
          {/* Field value chips (click to chart) — own row */}
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
              {lang === 'th' ? 'คลิกเพื่อเลือก • เลือกหลายตัวเพื่อเปรียบเทียบ' : 'Click to select • pick multiple to compare'}
            </div>
            {(() => {
              // One chip renderer for both layouts — flat for a simple device,
              // grouped once a meter reports enough values to be confusing.
              // Two sizes. A dimensionless % (THD, unbalance) doesn't earn the
              // same box as a 6-digit kWh reading, and a power analyser has 23
              // of them — the compact form is what keeps the strip off the chart.
              const chip = (key: string, compact = false) => {
                const fm = FIELD_META[key];
                const val = device.data[key];
                const unit = device.units?.[key] || fm?.unit || '';
                const active = activeFields.includes(key);
                // Unselected cards are neutral; colour is reserved for what is
                // actually plotted, and the number matches the chart's order.
                const color = fieldColorMap[key];
                const seriesNo = active ? activeFields.indexOf(key) + 1 : 0;
                const shown = val !== undefined ? fmtMetric(val, unit, key) : NO_VALUE;
                // A 228335.2 dragged the whole Energy group wide; the exact
                // figure stays one hover away.
                const short = shown.length > 7 && Math.abs(Number(val)) >= 10000
                  ? `${(Number(val) / 1000).toFixed(1)}k`
                  : shown;
                const label = fieldLabel(key, lang, device.fieldNames?.[key]);
                const title = `${label}${val !== undefined ? ` · ${shown}${unit ? ` ${unit}` : ''}` : ''}`;
                const frame: React.CSSProperties = {
                  fontFamily: 'inherit', cursor: 'pointer', textAlign: 'left', transition: 'all .15s',
                  borderRadius: compact ? 10 : 12,
                  border: `1px solid ${active ? color : 'var(--glass-border)'}`,
                  background: active ? `color-mix(in srgb, ${color} 10%, var(--bg-surface))` : 'var(--bg-surface)',
                  boxShadow: active ? `0 2px 10px color-mix(in srgb, ${color} 22%, transparent)` : 'none',
                };

                if (compact) {
                  return (
                    <button key={key} onClick={() => toggleField(key)} title={title} style={{
                      ...frame, display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', minWidth: 108,
                    }}>
                      {active && (
                        <span style={{
                          minWidth: 13, height: 13, borderRadius: 4, flexShrink: 0,
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          background: color, color: '#fff', fontSize: 8.5, fontWeight: 800,
                        }}>{seriesNo}</span>
                      )}
                      <span style={{ fontSize: 10, fontWeight: 700, color: active ? color : 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.03em', whiteSpace: 'nowrap' }}>
                        {label}
                      </span>
                      <span style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 800, color: active ? color : 'var(--text-primary)', whiteSpace: 'nowrap' }}>
                        {short}<span style={{ fontSize: 9.5, fontWeight: 600, marginLeft: 2, color: 'var(--text-muted)' }}>{unit}</span>
                      </span>
                    </button>
                  );
                }

                return (
                  <button key={key} onClick={() => toggleField(key)} title={title} style={{
                    ...frame, display: 'flex', flexDirection: 'column', gap: 5, padding: '10px 12px', minWidth: 128,
                  }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%' }}>
                      <span style={{ width: 22, height: 22, borderRadius: 7, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: `color-mix(in srgb, ${active ? color : 'var(--text-muted)'} 13%, transparent)` }}>
                        {fieldIcon(key, active ? color : 'var(--text-muted)', 13)}
                      </span>
                      <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</span>
                      {active && (
                        // Ties this card to its line without relying on colour
                        // alone, for the series that end up near each other.
                        <span style={{
                          marginLeft: 'auto', minWidth: 15, height: 15, borderRadius: 5, flexShrink: 0,
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          background: color, color: '#fff', fontSize: 9, fontWeight: 800,
                        }}>{seriesNo}</span>
                      )}
                    </span>
                    <span style={{ fontSize: 20, fontWeight: 800, color: active ? color : 'var(--text-primary)', lineHeight: 1.1 }}>
                      {short}<span style={{ fontSize: 11, fontWeight: 600, marginLeft: 4, color: 'var(--text-muted)' }}>{unit}</span>
                    </span>
                  </button>
                );
              };
              const moreBtn = fieldsOverflow ? (
                <button
                  onClick={() => setFieldsExpanded((v) => !v)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '7px 14px', borderRadius: 10, minWidth: 76,
                    fontFamily: 'inherit', fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all .15s',
                    border: '1px dashed var(--glass-border)', background: 'transparent', color: 'var(--text-muted)',
                  }}
                >
                  {fieldsExpanded
                    ? (lang === 'th' ? 'ย่อ' : 'Show less')
                    : `${lang === 'th' ? 'ดูทั้งหมด' : 'Show all'} (${dataFields.length})`}
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: fieldsExpanded ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
              ) : null;

              if (!fieldGroups) {
                return (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'stretch' }}>
                    {visibleFields.map((k) => chip(k))}
                    {moreBtn}
                  </div>
                );
              }

              return (
                // Each group is its own card. They flow side by side and take
                // a width proportional to how many chips they hold, so a
                // six-value Voltage group isn't squeezed next to a two-value one.
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
                  {fieldGroups.map((g) => {
                    const allOn = g.keys.every((k) => activeFields.includes(k));
                    const gc = FIELD_GROUP_COLOR[g.id];
                    const anyOn = g.keys.some((k) => activeFields.includes(k));
                    const dense = g.keys.length > FIELD_GROUP_DENSE_MIN;
                    return (
                      <div
                        key={g.id}
                        style={{
                          display: 'flex', flexDirection: 'column', gap: 8,
                          padding: '10px 12px 12px', borderRadius: 16,
                          border: `1px solid ${anyOn ? `color-mix(in srgb, ${gc} 45%, var(--glass-border))` : 'var(--glass-border)'}`,
                          // Lighter than before: the chips inside are neutral
                          // now, so the group tint no longer competes with them.
                          background: `color-mix(in srgb, ${gc} 2.5%, var(--bg-elevated))`,
                          // A big group takes its own row — otherwise it drags
                          // the small groups into a staircase of odd heights.
                          flex: dense ? '1 1 100%' : `1 1 ${Math.min(g.keys.length, 3) * 150}px`,
                        }}
                      >
                        {/* group header */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ width: 8, height: 8, borderRadius: 3, background: gc, flexShrink: 0 }} />
                          <span style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
                            {lang === 'th' ? FIELD_GROUP_LABEL[g.id].th : FIELD_GROUP_LABEL[g.id].en}
                          </span>
                          <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)' }}>{g.keys.length}</span>
                          {g.keys.length > 1 && (
                            <button
                              onClick={() => toggleGroup(g.keys)}
                              title={lang === 'th' ? 'พล็อตทุกค่าในกลุ่มนี้พร้อมกัน' : 'Plot every value in this group together'}
                              style={{
                                marginLeft: 'auto', padding: '2px 9px', borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit',
                                fontSize: 10, fontWeight: 700, transition: 'all .15s',
                                border: `1px solid ${allOn ? gc : 'var(--glass-border)'}`,
                                background: allOn ? `color-mix(in srgb, ${gc} 14%, transparent)` : 'transparent',
                                color: allOn ? gc : 'var(--text-muted)',
                              }}
                            >
                              {allOn ? (lang === 'th' ? 'ล้าง' : 'clear') : (lang === 'th' ? 'เลือกทั้งหมด' : 'select all')}
                            </button>
                          )}
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: dense ? 6 : 8, alignItems: 'stretch' }}>
                          {g.keys.map((k) => chip(k, dense))}
                        </div>
                      </div>
                    );
                  })}
                  {moreBtn && <div style={{ display: 'flex', alignItems: 'center', flex: '0 0 auto' }}>{moreBtn}</div>}
                </div>
              );
            })()}
          </div>

          {/* Controls toolbar: date range + custom dates + interval */}
          <div style={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px 20px',
            padding: '4px 0 12px', borderBottom: '1px solid var(--border-subtle)',
          }}>
            {/* Granularity (dropdown) */}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginRight: 2 }}>{lang === 'th' ? 'ความละเอียด' : 'Interval'}</span>
              <select
                value={chartInterval}
                onChange={(e) => setChartInterval(e.target.value)}
                style={{ ...dateInputStyle, cursor: 'pointer', paddingRight: 8 }}
              >
                {INTERVAL_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            {/* Date range (from → to) */}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginRight: 2 }}>{t('dateRange', lang)}</span>
              <input
                type="date"
                value={customFrom}
                max={customTo || undefined}
                onChange={(e) => setCustomFrom(e.target.value)}
                style={dateInputStyle}
              />
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{lang === 'th' ? 'ถึง' : 'to'}</span>
              <input
                type="date"
                value={customTo}
                min={customFrom || undefined}
                onChange={(e) => setCustomTo(e.target.value)}
                style={dateInputStyle}
              />
            </div>
            {/* Plot — applies the granularity + dates to the chart */}
            <button
              onClick={applyPlot}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 16px', borderRadius: 999,
                fontSize: 12, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', whiteSpace: 'nowrap',
                border: 'none', background: 'var(--accent)', color: '#fff', transition: 'all .15s',
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 17 9 11 13 15 21 7" /><polyline points="14 7 21 7 21 14" /></svg>
              {lang === 'th' ? 'แสดงกราฟ' : 'Plot'}
            </button>
            {customActive && (
              <button
                onClick={() => { setCustomFrom(''); setCustomTo(''); setAppliedFrom(''); setAppliedTo(''); }}
                title={lang === 'th' ? 'ล้างช่วงวันที่' : 'Clear dates'}
                style={{ ...dateInputStyle, cursor: 'pointer', color: 'var(--text-muted)', padding: '4px 10px' }}
              >
                ✕
              </button>
            )}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Export current series to Excel — next to interval */}
            <button
              onClick={exportExcel}
              disabled={exporting || !dbUser}
              title={!dbUser ? (lang === 'th' ? 'ใช้ได้เฉพาะข้อมูลจริง' : 'Available for live data only') : (lang === 'th' ? 'ส่งออกเป็น Excel' : 'Export to Excel')}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 999,
                fontSize: 11.5, fontWeight: 700, fontFamily: 'inherit',
                cursor: exporting || !dbUser ? 'not-allowed' : 'pointer',
                border: '1px solid var(--green)',
                background: 'color-mix(in srgb, var(--green) 12%, transparent)',
                color: 'var(--green)', opacity: !dbUser ? 0.45 : 1, whiteSpace: 'nowrap', transition: 'all .15s',
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              {exporting ? (lang === 'th' ? 'กำลังส่งออก…' : 'Exporting…') : 'Excel'}
            </button>
            {/* Export the chart image to PDF — next to Excel */}
            <button
              onClick={exportPdf}
              disabled={exportingPdf}
              title={lang === 'th' ? 'ส่งออกเป็น PDF' : 'Export to PDF'}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 999,
                fontSize: 11.5, fontWeight: 700, fontFamily: 'inherit',
                cursor: exportingPdf ? 'not-allowed' : 'pointer',
                border: '1px solid var(--red)',
                background: 'color-mix(in srgb, var(--red) 12%, transparent)',
                color: 'var(--red)', whiteSpace: 'nowrap', transition: 'all .15s',
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              {exportingPdf ? (lang === 'th' ? 'กำลังส่งออก…' : 'Exporting…') : 'PDF'}
            </button>
            {/* Export the chart image to PNG — next to PDF */}
            <button
              onClick={exportPng}
              disabled={exportingPng}
              title={lang === 'th' ? 'ส่งออกเป็น PNG' : 'Export to PNG'}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 999,
                fontSize: 11.5, fontWeight: 700, fontFamily: 'inherit',
                cursor: exportingPng ? 'not-allowed' : 'pointer',
                border: '1px solid var(--blue)',
                background: 'color-mix(in srgb, var(--blue) 12%, transparent)',
                color: 'var(--blue)', whiteSpace: 'nowrap', transition: 'all .15s',
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              {exportingPng ? (lang === 'th' ? 'กำลังส่งออก…' : 'Exporting…') : 'PNG'}
            </button>
            </div>
          </div>
          {/* Full-width chart — seamless inside the shared frame */}
          <div>
            <DeviceFieldChart
              activeFields={activeFields}
              fieldColors={fieldColorMap}
              chartRange={chartRange}
              currentValues={device.data}
              macId={device.sn}
              isDbDevice={!!dbUser}
              height={520}
              interval={appliedInterval}
              from={appliedActive ? appliedFrom : undefined}
              to={appliedActive ? appliedTo : undefined}
            />
          </div>
        </div>
      )}

      {/* Energy usage table — shown when the device has a cumulative kWh meter
          (any energy / 3-phase meter). Hourly/Daily selectable; Usage = End − Start. */}
      {device && deviceEnergyFields(device).length > 0 && (
        <MonitorEnergyTable device={device} lang={lang} enabled={!!dbUser} />
      )}

      {/* Event log — framed to match the chart panel, with manual + auto refresh */}
      <div style={{ marginTop: 16, borderRadius: 16, border: '1px solid var(--glass-border)', background: 'var(--bg-surface)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ width: 30, height: 30, borderRadius: 9, background: 'linear-gradient(135deg,#6366F1,#0EA5E9)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, boxShadow: '0 3px 10px rgba(99,102,241,0.35)' }}>📋</span>
            <span style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.2px' }}>{t('eventLog', lang)}</span>
            {totalEvents > 0 && <span style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--text-muted)', padding: '2px 9px', borderRadius: 999, background: 'color-mix(in srgb, var(--text-muted) 12%, transparent)' }}>{totalEvents.toLocaleString()}</span>}
          </span>
          <button
            onClick={fetchEvents}
            disabled={eventsLoading || !dbUser}
            title={lang === 'th' ? 'รีเฟรช' : 'Refresh'}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 999,
              fontSize: 11.5, fontWeight: 700, fontFamily: 'inherit', cursor: eventsLoading || !dbUser ? 'not-allowed' : 'pointer',
              border: '1px solid var(--glass-border)', background: 'transparent', color: 'var(--text-secondary)', opacity: !dbUser ? 0.45 : 1,
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: eventsLoading ? 'rotate(180deg)' : 'none', transition: 'transform .3s' }}><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>
            {eventsLoading ? (lang === 'th' ? 'กำลังโหลด…' : 'Loading…') : (lang === 'th' ? 'รีเฟรช' : 'Refresh')}
          </button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          {eventsLoading ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>{t('loadingChart', lang)}</div>
          ) : events.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>{t('noEvents', lang)}</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', background: 'color-mix(in srgb, var(--text-muted) 6%, transparent)' }}>
                  <th style={{ padding: '10px 16px', fontWeight: 700, whiteSpace: 'nowrap' }}>{t('time', lang)}</th>
                  <th style={{ padding: '10px 16px', fontWeight: 700 }}>{t('severity', lang)}</th>
                  <th style={{ padding: '10px 16px', fontWeight: 700 }}>{t('eventName', lang)}</th>
                  <th style={{ padding: '10px 16px', fontWeight: 700 }}>{t('devices', lang)}</th>
                  <th style={{ padding: '10px 16px', fontWeight: 700 }}>{t('detail', lang)}</th>
                </tr>
              </thead>
              <tbody>
                {events.map((ev, i) => {
                  const sv = SV_STYLE[ev.sv] || SV_STYLE.info;
                  const zebra = i % 2 ? 'color-mix(in srgb, var(--text-muted) 4%, transparent)' : 'transparent';
                  return (
                    <tr
                      key={i}
                      style={{ borderTop: '1px solid var(--border-subtle)', transition: 'background .15s', background: zebra }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-elevated)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = zebra)}
                    >
                      <td style={{ padding: '11px 16px', color: 'var(--text-muted)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{ev.time}</td>
                      <td style={{ padding: '11px 16px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 11px', borderRadius: 999, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px', background: sv.bg, color: sv.fg, border: `1px solid color-mix(in srgb, ${sv.fg} 30%, transparent)` }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: sv.fg, boxShadow: `0 0 6px ${sv.fg}` }} />
                          {ev.sv}
                        </span>
                      </td>
                      <td style={{ padding: '11px 16px', fontWeight: 700, color: sv.fg }}>{ev.title}</td>
                      <td style={{ padding: '11px 16px', color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: 12 }}>{ev.device || '—'}</td>
                      <td style={{ padding: '11px 16px', color: 'var(--text-secondary)' }}>{ev.desc}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        {totalEvents > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 16px', borderTop: '1px solid var(--border-subtle)', fontSize: 12, color: 'var(--text-muted)' }}>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{rangeStart}–{rangeEnd} / {totalEvents}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button onClick={() => setEventsPage((p) => Math.max(1, p - 1))} disabled={eventsPage <= 1 || eventsLoading} style={pagerBtn(eventsPage <= 1 || eventsLoading)} aria-label="Previous page">‹</button>
              <span style={{ minWidth: 64, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>{eventsPage} / {totalPages}</span>
              <button onClick={() => setEventsPage((p) => Math.min(totalPages, p + 1))} disabled={eventsPage >= totalPages || eventsLoading} style={pagerBtn(eventsPage >= totalPages || eventsLoading)} aria-label="Next page">›</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
